/**
 * Light Cycles: the arena grid and the shapes both the server simulation and the client
 * renderer work with. Portrait arena so a phone shows the whole grid without letterboxing.
 */

export const ARENA_W = 40
export const ARENA_H = 56
export const ARENA_CELLS = ARENA_W * ARENA_H

export const MAX_CYCLES = 8
/** The host can't race alone — a second cycle (human or bot) is required. */
export const MIN_CYCLES = 2
export const MAX_BOTS = 3
export const ROUND_OPTIONS = [3, 5, 10] as const
export type RoundCount = (typeof ROUND_OPTIONS)[number]
/** Turns a player may have waiting: enough for a quick double-tap around a corner, no more. */
export const MAX_QUEUED_TURNS = 2

/** Clockwise from up, so a right turn is `(h + 1) & 3`. */
export type Heading = 0 | 1 | 2 | 3
export const HEADINGS: readonly Heading[] = [0, 1, 2, 3]
export const DX: readonly number[] = [0, 1, 0, -1]
export const DY: readonly number[] = [-1, 0, 1, 0]

export function turnLeft(h: Heading): Heading {
  return ((h + 3) & 3) as Heading
}
export function turnRight(h: Heading): Heading {
  return ((h + 1) & 3) as Heading
}
export function opposite(h: Heading): Heading {
  return ((h + 2) & 3) as Heading
}
/** A turn is only ever sideways: straight ahead and reversing are both ignored. */
export function isPerpendicular(a: Heading, b: Heading): boolean {
  return ((a - b) & 1) === 1
}

export function inBounds(x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < ARENA_W && y < ARENA_H
}
export function cellIndex(x: number, y: number): number {
  return y * ARENA_W + x
}

/**
 * One cycle as sent over the wire. `path` is an axis-aligned polyline `[x0, y0, x1, y1, …]`
 * whose last pair is always the head; a straight run is two pairs however long it is.
 */
export interface CycleSnapshot {
  id: string
  x: number
  y: number
  heading: Heading
  alive: boolean
  /** The tick on which the cycle crashed, or null while it lives. */
  diedAt: number | null
  path: number[]
}

/** The simulation state: cycles plus an occupancy grid (0 = empty, else cycle index + 1). */
export interface RoundState {
  no: number
  tick: number
  cycles: CycleSnapshot[]
  cells: Uint8Array
}
